/**
 * RuleQuietAdders — the genuinely optional refinement, kept quiet until used.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Only the skip-list lives here, and it earns the "Optional" label: by default
 * nothing is skipped. The date window deliberately does NOT — unset, a rule
 * still inherits a window, so calling it optional would be a lie. It sits with
 * the conditions it measures instead.
 */

import { FC, useMemo } from 'react';
import { useOptionalFlowOptimizerLivePreview } from '../../../context/FlowOptimizerLivePreviewContext';
import { IgnorePathsSelect, ignorePathOptions } from '../../../ignore-paths';
import type { ExcludeViewGroup } from '../../../utils/exclude-view-model';

export interface RuleQuietAddersProps {
  group: ExcludeViewGroup;
  onChange: (next: ExcludeViewGroup) => void;
}

export const RuleQuietAdders: FC<RuleQuietAddersProps> = ({ group, onChange }) => {
  const preview = useOptionalFlowOptimizerLivePreview();
  const pathOptions = useMemo(
    () => ignorePathOptions(preview?.result?.items),
    [preview?.result],
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[12.5px] font-semibold text-muted-foreground">
        Optional
      </span>
      {/* Paths-only, like the formula card: a rule scoped to landers/offers is
          never evaluated with a path's bank id, so a list set here could never
          skip anything. An already-stored list still shows, so it can be
          cleared rather than hidden while quietly persisting. */}
      {group.appliesTo.includes('paths') || group.ignoredPathBankIds?.length ? (
        <IgnorePathsSelect
          kind="rule"
          value={group.ignoredPathBankIds ?? []}
          options={pathOptions}
          optionsReady={Boolean(preview?.result)}
          onChange={(ignoredPathBankIds) =>
            onChange({ ...group, ignoredPathBankIds })
          }
        />
      ) : null}
    </div>
  );
};

export default RuleQuietAdders;
